import React, { useEffect, useState, useRef } from "react";
import { useAxiosInstance } from "../../../services/Interceptor";
import PayWidget from "../../../components/PayWidget";
import {
  GetEventCombinePrice,
  GetEventsByEventId,
  RegisterAthlete,
} from "../../../services/EventsService";
import { PostPayment } from "../../../services/PaymentService";
import { useNavigate } from "react-router-dom";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { getCouponPrice } from "../../../services/PublicServices";
import couponTypes from "../../../enums/coupon-types";

const WIDGET_READY_FALLBACK_MS = 5000;

export default function ModalIntelliPay({
  eventId,
  origin,
  onCloseAll,
  tShirtSize,
  offerAcceptanceStatus,
  offerAcceptanceSchool,
}) {
  const apiInstance = useAxiosInstance();
  const [combinePrice, setCombinePrice] = useState(0);
  const [positionDrillPrice, setPositionDrillPrice] = useState(0);

  const { user } = useAuthUser() || {};
  const [selectedType, setSelectedType] = useState(null);
  const buttonRef = useRef(null);
  const widgetClickedRef = useRef(false);
  const [eventInfo, setEventInfo] = useState();
  const [loading, setLoading] = useState(false);
  const [isPricingLoaded, setIsPricingLoaded] = useState(false);
  const navigate = useNavigate();

  const [couponCode, setCouponCode] = useState("");
  const getDefaultDiscountState = () => ({
    couponType: "",
    combine: {
      discountAmount: 0,
      basePercentage: 0,
      discountType: "",
    },
    combineAndPosition: {
      discountAmount: 0,
      basePercentage: 0,
      discountType: "",
    },
  });

  const [discountDetails, setDiscountDetails] = useState(
    getDefaultDiscountState,
  );
  const isModalOpen = true;

  const [agreedToWaiver, setAgreedToWaiver] = useState(false);
  const [waiverTouched, setWaiverTouched] = useState(false);
  const [hasPositionDrills, setHasPositionDrills] = useState(false);
  const [isWidgetReady, setIsWidgetReady] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    setSelectedType(null);
    setLoading(false);
    setCouponCode("");
    setDiscountDetails(getDefaultDiscountState());
    setAgreedToWaiver(false);
  }, []);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await GetEventsByEventId(apiInstance, eventId);
        if (data) {
          setEventInfo(`${data.date} - ${data.name} - ${data.city}`);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadEvents();
  }, [apiInstance, eventId]);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const combinePriceRes = await GetEventCombinePrice(
          apiInstance,
          eventId,
        );
        setCombinePrice(combinePriceRes?.data?.combinePrice || 0);
        setHasPositionDrills(combinePriceRes?.data?.hasPositionDrills);
        setPositionDrillPrice(combinePriceRes?.data?.positionDrillPrice);
        setIsPricingLoaded(true);
      } catch (error) {
        console.error("Error fetching pricing:", error);
        setIsPricingLoaded(true);
      }
    };
    fetchPricing();
  }, [apiInstance]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedType) return;

    if (isWidgetReady && buttonRef.current && !widgetClickedRef.current) {
      widgetClickedRef.current = true;
      buttonRef.current.click();
      setLoading(false);
      return;
    }

    if (widgetClickedRef.current) return;

    const fallback = setTimeout(() => {
      setLoading(false);
      setSelectedType(null);
      setIsWidgetReady(false);
      toast.error("Payment widget failed to load. Please try again.", {
        containerId: "modal",
      });
    }, WIDGET_READY_FALLBACK_MS);

    return () => clearTimeout(fallback);
  }, [isWidgetReady, selectedType]);

  const applyCouponCode = async () => {
    try {
      localStorage.removeItem("appliedCouponCode");

      const response = await getCouponPrice(apiInstance, couponCode);

      if (!response || response.type === couponTypes.invalid) {
        setDiscountDetails(getDefaultDiscountState());
        toast.error("Invalid coupon code", { containerId: "modal" });
        return;
      }

      const amount = Number(response.amount || 0);
      const type = String(response.type || "").trim();
      const discountType = String(response.discountType || "").trim();

      if (type === couponTypes.subscription) {
        setDiscountDetails(getDefaultDiscountState());
        toast.error("Subscription coupons are not valid for this purchase.", {
          containerId: "modal",
        });
        return;
      }

      const combineBase = Number(combinePrice || 0);
      const combineAndPositionBase =
        Number(combinePrice || 0) + Number(positionDrillPrice || 0);

      const calculateDiscount = (baseAmount) => {
        if (discountType === "Percent Off") {
          return Math.min((baseAmount * amount) / 100, baseAmount);
        }
        return Math.min(amount, baseAmount);
      };

      const nextDiscountDetails = getDefaultDiscountState();
      nextDiscountDetails.couponType = type;

      nextDiscountDetails.combine.discountType = discountType;
      nextDiscountDetails.combineAndPosition.discountType = discountType;

      if (type === couponTypes.combine || type === "Combine") {
        const leftDiscount = calculateDiscount(combineBase);
        const rightDiscount = calculateDiscount(combineBase);

        nextDiscountDetails.combine.discountAmount = leftDiscount;
        nextDiscountDetails.combineAndPosition.discountAmount = rightDiscount;

        if (discountType === "Percent Off") {
          nextDiscountDetails.combine.basePercentage = amount;
          nextDiscountDetails.combineAndPosition.basePercentage = amount;
        }

        setDiscountDetails(nextDiscountDetails);

        toast.success("Combine coupon applied successfully.", {
          containerId: "modal",
        });

        localStorage.setItem("appliedCouponCode", couponCode);
        return;
      }
      if (type === couponTypes.positionDrills || type === "PositionDrills") {
        if (!hasPositionDrills) {
          setDiscountDetails(getDefaultDiscountState());
          toast.error("Position Drill coupon is not valid for this event.", {
            containerId: "modal",
          });
          return;
        }

        const rightDiscount = calculateDiscount(positionDrillPrice);

        nextDiscountDetails.combine.discountAmount = 0;
        nextDiscountDetails.combineAndPosition.discountAmount = rightDiscount;

        if (discountType === "Percent Off") {
          nextDiscountDetails.combineAndPosition.basePercentage = amount;
        }

        setDiscountDetails(nextDiscountDetails);

        toast.success("Position Drill coupon applied successfully.", {
          containerId: "modal",
        });

        localStorage.setItem("appliedCouponCode", couponCode);
        return;
      }

      if (
        type === couponTypes.CombineAndPositionDrills ||
        type === "CombineAndPositionDrills"
      ) {
        const leftDiscount = calculateDiscount(combineBase);

        const rightDiscount =
          discountType === "Percent Off"
            ? calculateDiscount(combineAndPositionBase)
            : Math.min(amount, combineAndPositionBase);

        nextDiscountDetails.combine.discountAmount = leftDiscount;
        nextDiscountDetails.combineAndPosition.discountAmount = rightDiscount;

        if (discountType === "Percent Off") {
          nextDiscountDetails.combine.basePercentage = amount;
          nextDiscountDetails.combineAndPosition.basePercentage = amount;
        }

        setDiscountDetails(nextDiscountDetails);

        toast.success(
          "Combine + Position Drills coupon applied successfully.",
          {
            containerId: "modal",
          },
        );

        localStorage.setItem("appliedCouponCode", couponCode);
        return;
      }

      setDiscountDetails(getDefaultDiscountState());
      toast.error("Coupon not applicable.", { containerId: "modal" });
    } catch (error) {
      console.error(error);
      setDiscountDetails(getDefaultDiscountState());
      toast.error("Invalid coupon code", { containerId: "modal" });
    }
  };

  const getCombineTotal = () => {
    const base = Number(combinePrice || 0);
    return Math.max(
      base - Number(discountDetails.combine.discountAmount || 0),
      0,
    );
  };

  const getCombinePositionTotal = () => {
    if (!hasPositionDrills) return getCombineTotal();
    const base = Number(combinePrice || 0) + Number(positionDrillPrice || 0);
    return Math.max(
      base - Number(discountDetails.combineAndPosition.discountAmount || 0),
      0,
    );
  };
  const handleCombinePass = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!agreedToWaiver) {
      setWaiverTouched(true);
      return;
    }

    if (!isPricingLoaded || loading) {
      return;
    }

    const total = getCombineTotal();

    if (total <= 0) {
      try {
        setLoading(true);
        const userId = user?.id;
        const regRes = await RegisterAthlete(
          apiInstance,
          eventId,
          userId,
          agreedToWaiver,
          tShirtSize,
          offerAcceptanceStatus,
          offerAcceptanceSchool,
        );
        if (regRes.status === 200) {
          const paymentPayload = {
            userId: userId,
            amount: 0,
            isCombine: true,
            isPositionDrill: false,
            isAnnualSubscription: false,
            isMonthlySubscription: false,
            isCash: false,
            eventId: eventId,
            transactionId: "",
            intellipayToken: "",
          };

          const paidRes = await PostPayment(apiInstance, paymentPayload);
          if (paidRes.status === 200) {
            toast.success(
              "Registration and payment successful!",
              { containerId: "modal" },
              {
                position: "top-right",
              },
            );
            redirectAfterPayment();
          } else {
            toast.error(
              "Payment after registration failed.",
              { containerId: "modal" },
              {
                position: "top-right",
              },
            );
          }
        } else {
          toast.error(
            "Registration failed.",
            { containerId: "modal" },
            { position: "top-right" },
          );
        }
      } catch (err) {
        toast.error(
          "Something went wrong.",
          { containerId: "modal" },
          { position: "top-right" },
        );
        console.error(err);
      } finally {
        setLoading(false);
      }
      return;
    }

    setIsWidgetReady(false);
    widgetClickedRef.current = false;
    setSelectedType("combine");
    setLoading(true);
  };

  const handleCombinePositionDrill = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!agreedToWaiver) {
      setWaiverTouched(true);
      return;
    }

    if (!isPricingLoaded || loading) {
      return;
    }

    const total = getCombinePositionTotal();

    if (total <= 0) {
      try {
        setLoading(true);
        const userId = user?.id;
        const regRes = await RegisterAthlete(
          apiInstance,
          eventId,
          userId,
          agreedToWaiver,
          tShirtSize,
          offerAcceptanceStatus,
          offerAcceptanceSchool,
        );
        if (regRes.status === 200) {
          const combineDrillPayload = [
            {
              userId: userId,
              amount: 0,
              isCombine: true,
              isPositionDrill: false,
              isAnnualSubscription: false,
              isMonthlySubscription: false,
              isCash: false,
              eventId: eventId,
              transactionId: "",
              intellipayToken: "",
            },
            {
              userId: userId,
              amount: 0,
              isCombine: false,
              isPositionDrill: true,
              isAnnualSubscription: false,
              isMonthlySubscription: false,
              isCash: false,
              eventId: eventId,
              transactionId: "",
              intellipayToken: "",
            },
          ];

          for (const drill of combineDrillPayload) {
            const paidRes = await PostPayment(apiInstance, drill);
            if (paidRes.status !== 200) {
              toast.error(
                "Payment after registration failed.",
                { containerId: "modal" },
                {
                  position: "top-right",
                },
              );
              return;
            }
          }

          toast.success(
            "Registration and payment successful!",
            { containerId: "modal" },
            {
              position: "top-right",
            },
          );
          redirectAfterPayment();
        } else {
          toast.error(
            "Event registration failed.",
            { containerId: "modal" },
            {
              position: "top-right",
            },
          );
        }
      } catch (err) {
        toast.error(
          "Something went wrong.",
          { containerId: "modal" },
          { position: "top-right" },
        );
        console.error(err);
      } finally {
        setLoading(false);
      }
      return;
    }

    setIsWidgetReady(false);
    widgetClickedRef.current = false;
    setSelectedType("combinePosition");
    setLoading(true);
  };

  const formatPrice = (value) => `$${Number(value).toFixed(2)}`;

  const redirectAfterPayment = () => {
    const page = origin;
    const path =
      page === "DashboardEvent"
        ? "/athlete/dashboard"
        : page === "athletes-events"
          ? "/athlete/events"
          : page === "SignInEvent"
            ? "/events"
            : "/";
    window.location.href = path;
  };

  const closePriceOptions = () => {
    setSelectedType(null);
    setLoading(false);
    setCouponCode("");
    setDiscountDetails(getDefaultDiscountState());

    if (typeof onCloseAll === "function") {
      onCloseAll();
      return;
    }

    if (origin === "DashboardEvent") navigate("/athlete/dashboard/");
    if (origin === "athletes-events") navigate("/athlete/events");
  };

  function WaiverBlock({ agreed, setAgreed }) {
    const [open, setOpen] = React.useState(true);

    return (
      <div className="mx-4 mt-2 mb-3 border border-gray-200 rounded-md bg-gray-50">
        {waiverTouched && !agreedToWaiver && (
          <p className="text-red-500 text-xs mt-1">
            You must accept the waiver before registering.
          </p>
        )}

        <div className="flex items-center justify-between px-3 py-2">
          <div className="text-sm font-semibold text-gray-900">
            Waiver & Liability Agreement
          </div>

          <button
            type="button"
            className="text-sm text-blue-700 hover:underline"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "Hide" : "View"}
          </button>
        </div>

        {open && (
          <div className="px-3 pb-3">
            <div className="h-40 overflow-y-auto border p-3 bg-white rounded-md text-sm leading-6">
              <p className="text-sm">
                I hereby acknowledge that I am, or my child is, voluntarily
                participating in a Combine organized by Track It Verified, LLC
                (“the Activity”). In consideration for being permitted to
                participate in this activity, I hereby agree as follows:
              </p>

              <ol className="list-decimal list-inside text-sm mt-2">
                <li className="pb-3">
                  I understand and acknowledge that there are certain risks and
                  dangers associated with the Activity, including but not
                  limited to: getting injured while performing the combine
                  drills.
                </li>
                <li className="pb-3">
                  On behalf of myself (if 18 or older) or on behalf of my child,
                  I/we voluntarily assume all risks related to participation in
                  the Activity and agree to release and hold harmless Track It
                  Verified, LLC, its officers, employees, volunteers, and agents
                  from any and all claims, damages, liabilities, costs, and
                  expenses that may arise as a result of participation in the
                  Activity, including but not limited to injury or loss.
                </li>
                <li className="pb-3">
                  On behalf of myself (if 18 or older) or on behalf of my child,
                  I/we agree to indemnify and defend Track It Verified, LLC, its
                  officers, employees, volunteers, and agents against any and
                  all claims, damages, liabilities, costs, and expenses,
                  including attorney fees, arising from or in connection with
                  participation in the Activity.
                </li>
                <li className="pb-3">
                  On behalf of myself (if 18 or older) or on behalf of my child,
                  I/we certify that I or my child is in good health and have/has
                  no physical or mental conditions that prevent me or my child
                  from safely participating in the Activity.
                </li>
                <li className="pb-3">
                  On behalf of myself (if 18 or older) or on behalf of my child,
                  I grant Track It Verified, LLC the right to use photographs,
                  video recordings, or other media of me or my child taken
                  during the Activity for promotional or marketing purposes.
                  (COPPA and additional terms...)
                </li>
              </ol>

              <p className="mt-1 text-sm">
                I HAVE READ THIS RELEASE, IF SIGNING MYSELF OR ON BEHALF OF MY
                CHILD, AND FULLY UNDERSTAND ITS TERMS, AND UNDERSTAND THAT I AM
                SIGNING FREELY AND VOLUNTARILY WITHOUT ANY INDUCEMENT.
              </p>
            </div>
          </div>
        )}

        <div className="px-3 pb-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="w-4 h-4"
              checked={agreedToWaiver}
              onChange={(e) => setAgreedToWaiver(e.target.checked)}
            />
            <label className="text-sm">
              I have read and agree to the Waiver & Liability Agreement.
            </label>
          </label>

          {!agreed && (
            <div className="text-xs text-red-600 mt-1">
              You must accept the waiver before registering.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          {loading && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[100]">
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                size="3x"
                className="text-blue-800"
              />
            </div>
          )}

          <div className="bg-white rounded-md shadow-xl w-full max-w-3xl flex flex-col max-h-[90vh] m-3 relative -mt-16">
            {/* Header */}
            <div className="flex justify-between items-center px-4 py-3">
              <h2 className="text-xl font-bold text-gray-900">
                Combine Registration
                <p className="text-sm text-gray-700 font-normal">
                  Choose the perfect plan to level up your performance
                </p>
              </h2>

              <button
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-full text-sm p-2 ml-auto inline-flex items-center -mt-4"
                onClick={closePriceOptions}
              >
                <FontAwesomeIcon
                  icon={faXmark}
                  className=" cursor-pointer text-lg"
                  onClick={closePriceOptions}
                />
              </button>
            </div>
            {/* Scrollable Content Area */}
            <WaiverBlock
              agreed={agreedToWaiver}
              setAgreed={setAgreedToWaiver}
            />
            <div className="p-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
                <div className=" flex flex-col justify-between border border-gray-300 p-4 rounded-md">
                  <div>
                    <div className="flex items-center mb-3">
                      <div className="text-xl mr-2">🏈</div>
                      <h3 className="text-xl font-bold text-black">
                        Combine Pass
                      </h3>
                    </div>
                    <div className="pl-0 xl:pl-6">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-700">
                          Combine Price:
                        </span>
                        <span className="text-sm text-black">
                          {formatPrice(combinePrice)}
                        </span>
                      </div>

                      {discountDetails.combine.discountAmount > 0 && (
                        <>
                          {discountDetails.combine.discountType ===
                            "Percent Off" && (
                            <div className="flex justify-between text-green-600 mb-1">
                              <span className="text-sm font-semibold">
                                Discount Percentage:
                              </span>
                              <span className="text-sm">
                                {discountDetails.combine.basePercentage}%
                              </span>
                            </div>
                          )}

                          <div className="flex justify-between text-green-600 mb-1">
                            <span className="text-sm font-semibold">
                              Discount Amount:
                            </span>
                            <span className="text-sm">
                              -
                              {formatPrice(
                                discountDetails.combine.discountAmount,
                              )}
                            </span>
                          </div>
                        </>
                      )}

                      <div className="border-t border-gray-300 pt-2 mt-2">
                        <div className="flex justify-between font-bold text-lg">
                          <span>Total:</span>
                          <span>{formatPrice(getCombineTotal())}</span>
                        </div>
                      </div>

                      <p className="text-sm text-gray-500 mt-3">One-Time</p>
                      <p className="text-sm text-gray-700 mb-2">{eventInfo}</p>
                      <div className="border-t border-gray-300 pt-3">
                        <h4 className="text-sm font-semibold mb-1 text-black">
                          What's included
                        </h4>
                        <ul className="text-sm text-gray-700 list-disc ml-5">
                          <li>Combine Participation</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="mt-5 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleCombinePass}
                    disabled={!isPricingLoaded || loading || !agreedToWaiver}
                  >
                    {!isPricingLoaded
                      ? "LOADING..."
                      : getCombineTotal() <= 0
                        ? "REGISTER FOR COMBINE"
                        : "PAY FOR COMBINE"}
                  </button>
                </div>

                {hasPositionDrills && (
                  <div className="flex flex-col justify-between border border-gray-300 p-4 rounded-md">
                    <div>
                      <div className="flex items-center mb-3">
                        <div className="text-xl mr-2">🏈</div>
                        <h3 className="text-xl font-bold text-black">
                          Combine + Position Drills
                        </h3>
                      </div>

                      <div className="pl-0 xl:pl-6">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-semibold text-gray-700">
                            Combine Price:
                          </span>
                          <span>{formatPrice(combinePrice)}</span>
                        </div>

                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-semibold text-gray-700">
                            Position Drill Price:
                          </span>
                          <span>{formatPrice(positionDrillPrice)}</span>
                        </div>

                        {discountDetails.combineAndPosition.discountAmount >
                          0 && (
                          <>
                            {discountDetails.combineAndPosition.discountType ===
                              "Percent Off" && (
                              <div className="flex justify-between text-green-600 mb-1">
                                <span className="text-sm font-semibold text-gray-700">
                                  Discount (
                                  {
                                    discountDetails.combineAndPosition
                                      .basePercentage
                                  }
                                  %)
                                  {discountDetails.couponType === "Combine" &&
                                    " of Combine Pass"}
                                </span>
                              </div>
                            )}

                            <div className="flex justify-between text-green-600 mb-1">
                              <span className="text-sm font-semibold text-gray-700">
                                Discount Amount:
                              </span>
                              <span>
                                -
                                {formatPrice(
                                  discountDetails.combineAndPosition
                                    .discountAmount,
                                )}
                              </span>
                            </div>
                          </>
                        )}

                        <div className="border-t pt-2 mt-2">
                          <div className="flex justify-between font-bold text-lg">
                            <span>Total:</span>
                            <span>
                              {formatPrice(getCombinePositionTotal())}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="mt-5 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm disabled:opacity-50"
                      onClick={handleCombinePositionDrill}
                      disabled={!isPricingLoaded || loading || !agreedToWaiver}
                    >
                      {getCombinePositionTotal() <= 0
                        ? "REGISTER FOR COMBINE + POSITION DRILL"
                        : "PAY FOR COMBINE + POSITION DRILL"}
                    </button>
                  </div>
                )}
              </div>
            </div>
            {/* Footer */}
            <div className="border-0 px-4 pb-6 pt-4 flex justify-start gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <label
                  htmlFor="coupon"
                  className="text-sm font-semibold text-black"
                >
                  Coupon Code:
                </label>
                <input
                  type="text"
                  id="coupon"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                  placeholder="Enter code"
                />
                <button
                  onClick={applyCouponCode}
                  className="bg-black text-white px-4 py-1 rounded text-sm"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>

          {selectedType && (
            <>
              <button
                data-ipayname="submit"
                className="ipayfield hidden"
                ref={buttonRef}
              ></button>
              <PayWidget
                key={`paywidget-${selectedType}-${eventId}`}
                userEmail={user?.email}
                userId={user?.id}
                paymentAmount={
                  selectedType === "combine"
                    ? getCombineTotal()
                    : getCombinePositionTotal()
                }
                eventId={eventId}
                subscriptionType={selectedType}
                onRegistrationSuccess={redirectAfterPayment}
                closeAction={true}
                onClose={redirectAfterPayment}
                agreedToWaiver={agreedToWaiver === true}
                tShirtSize={tShirtSize}
                offerAcceptanceStatus={offerAcceptanceStatus}
                offerAcceptanceSchool={offerAcceptanceSchool}
                onIntellipayReady={setIsWidgetReady}
              />
            </>
          )}
        </div>
      )}
    </>
  );
}
